import { WORD_CARD_KIND_LIST } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { primaryNavigationDestinationsSettingSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema._tables.user";
import { corpusSettingsSchema } from "@-/dev.improve.haniverse.lib.morpheme.corpus.settings";
import { customLevelPacksSchema } from "@-/dev.improve.haniverse.lib.level.custom";
import * as z from "@-/com.npmjs.zod";

const dateStringSchema = z.string().min(1);

const crudFields = {
  createdAt: dateStringSchema,
  deletedAt: z.nullable(dateStringSchema),
  id: z.string().min(1),
  updatedAt: z.nullable(dateStringSchema),
} as const;

const collectionSchema = z.strictObject({
  ...crudFields,
  icon: z.string().nullable(),
  isFavorites: z.boolean(),
  name: z.string(),
  sortOrder: z.number().int(),
  userId: z.string().nullable(),
});

const collectionMorphemeSchema = z.strictObject({
  ...crudFields,
  collectionId: z.string().min(1),
  morpheme: z.string().min(1),
});

const fsrsCardSchema = z.strictObject({
  ...crudFields,
  difficulty: z.number().finite(),
  due: dateStringSchema,
  elapsedDays: z.number().int(),
  lapses: z.number().int(),
  lastReview: z.nullable(dateStringSchema),
  learningSteps: z.number().int(),
  reps: z.number().int(),
  scheduledDays: z.number().int(),
  stability: z.number().finite(),
  state: z.number().int(),
});

const fsrsCardReviewLogSchema = z.strictObject({
  ...crudFields,
  difficulty: z.number().finite(),
  due: dateStringSchema,
  elapsedDays: z.number().int(),
  lastElapsedDays: z.number().int(),
  learningSteps: z.number().int(),
  rating: z.number().int(),
  review: dateStringSchema,
  scheduledDays: z.number().int(),
  stability: z.number().finite(),
  state: z.number().int(),
});

const morphemeNoteSchema = z.strictObject({
  ...crudFields,
  morpheme: z.string().min(1),
  text: z.string().nullable(),
});

const morphemeSearchVisitSchema = z.strictObject({
  ...crudFields,
  front: z.string().min(1),
  searchedAt: dateStringSchema,
});

const userCardSchema = z.strictObject({
  ...crudFields,
  // Optional rather than nullable-only: exports written before the column existed omit it, and
  // they must still import. A missing value means "not deferred".
  deferredUntil: z.optional(z.nullable(dateStringSchema)),
  fsrsCardId: z.string().nullable(),
  manuallyMasteredAt: z.nullable(dateStringSchema),
  // Optional for exports written before these columns existed.
  queuedAt: z.optional(z.nullable(dateStringSchema)),
  reviewRechargeAfter: z.optional(z.nullable(z.int())),
  skippedAt: z.nullable(dateStringSchema),
  startedStudyingAt: z.nullable(dateStringSchema),
});

const userCardReviewSchema = z.strictObject({
  ...crudFields,
  cardId: z.string().min(1),
  fsrsCardReviewLogId: z.string().min(1),
  // Optional for exports written before Han existed.
  hanEarned: z.optional(z.nullable(z.int())),
  reviewedAt: dateStringSchema,
  // Optional for exports written before the column existed.
  reviewRechargeAfter: z.optional(z.nullable(z.int())),
});

const userGraphemeMeaningCardSchema = z
  .strictObject({
    ...crudFields,
    description: z.string().nullable(),
    // Exports written before the column was dropped carry the stale content gloss; it is stripped
    // here.
    gloss: z.optional(z.string()),
    grapheme: z.string().min(1),
    userCardId: z.string().min(1),
  })
  .transform(({ gloss: _gloss, ...row }) => row);

const userGraphemePronunciationCardSchema = z
  .strictObject({
    ...crudFields,
    description: z.string().nullable(),
    // Exports written before the column was dropped carry the stale content gloss; it is stripped
    // here.
    gloss: z.optional(z.string()),
    grapheme: z.string().min(1),
    pinyin: z.string().regex(/^[a-z]+[0-4]$/u),
    userCardId: z.string().min(1),
  })
  .transform(({ gloss: _gloss, ...row }) => row);

const userWordSentenceCardSchema = z.strictObject({
  ...crudFields,
  description: z.string().nullable(),
  // Part of the card's identity: a `(word, pinyin)` has one card per kind, each with its own FSRS
  // state, so an export that dropped this would collapse three cards into one on import.
  kind: z.enum(WORD_CARD_KIND_LIST),
  // Multi-syllable, unlike the single-syllable grapheme pronunciation pinyin above: a word card
  // carries the reading for the whole word. The canonical stored form (`normalizeToneNumbersPinyin`)
  // separates syllables with single spaces (`shui4 jiao4`); unspaced input (`shui4jiao4`) is
  // accepted too. `ü` is stored as `v`.
  pinyin: z.string().regex(/^[a-z]+[0-4](?: ?[a-z]+[0-4])*$/u),
  userCardId: z.string().min(1),
  word: z.string().min(1),
});

const userQuizCardSchema = z.strictObject({
  ...crudFields,
  // The quiz's catalogue UUID: the card's identity, and how its bundled content is found.
  key: z.string().min(1),
  userCardId: z.string().min(1),
});

const userSentenceSchema = z.strictObject({
  ...crudFields,
  favoritedAt: z.nullable(dateStringSchema),
  // Keyed by the sentence text, not a static-DB id — static ids renumber on every rebuild.
  sentence: z.string().min(1),
  text: z.string().nullable(),
});

const userSlideSchema = z
  .strictObject({
    ...crudFields,
    completedAt: z.nullable(dateStringSchema),
    favoritedAt: z.nullable(dateStringSchema),
    // The slide's catalogue UUID, its identity. Nullable and defaulted because archives written
    // before the column existed still have to parse; a row without one cannot be matched to a
    // slide, and `normalizeActorDataExportIdentities` leaves it out of what is written.
    key: z.nullable(z.string().min(1)).default(null),
    // The identity column `key` replaced. Older archives carry it; it is stripped here.
    slug: z.optional(z.string()),
  })
  .transform(({ slug: _slug, ...row }) => row);

const userSchema = z.preprocess(
  (value) => {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
      return value;
    }
    if (!("bottomTabs" in value) || "primaryNavigationDestinations" in value) {
      return value;
    }
    const { bottomTabs, ...rest } = value;
    return { ...rest, primaryNavigationDestinations: bottomTabs };
  },
  z.strictObject({
    ...crudFields,
    // Older archives used `bottomTabs`; restores still accept that key via the preprocess above.
    primaryNavigationDestinations: primaryNavigationDestinationsSettingSchema.optional(),
    // Older archives predate the corpus filter; restores use the database default.
    corpusSettings: corpusSettingsSchema.optional(),
    // Old archives use the empty database default; new archives retain authored packs.
    customLevelPacks: customLevelPacksSchema.optional(),
    hanziScript: z.enum(["traditional", "simplified"]),
    hskVersion: z.enum(["hsk2", "hsk3", "hsk2026"]),
    isMicDrillsEnabled: z.boolean(),
    isPinyinFlashcardsEnabled: z.boolean(),
    isPinyinMnemonicsEnabled: z.boolean(),
    // Defaulted so a backup exported before quiz cards existed still imports.
    isQuizCardsInStatsEnabled: z.boolean().default(false),
    isSentenceCharacterDrillEnabled: z.boolean(),
    isSentenceSpokenDrillEnabled: z.boolean(),
    isToneColorsEnabled: z.boolean(),
    isWordSentenceCardsEnabled: z.boolean(),
    pinyinKeyboardLayout: z.enum([
      "qwerty",
      "ziranma",
      "sogou",
      "pinyin-jiajia",
      "weiruan",
      "xiaohe",
      "changyong",
    ]),
    reviewMode: z.enum(["manual", "answer"]),
    tone0ColorDarkHex: z.string(),
    tone0ColorDarkName: z.string(),
    tone0ColorLightHex: z.string(),
    tone0ColorLightName: z.string(),
    tone1ColorDarkHex: z.string(),
    tone1ColorDarkName: z.string(),
    tone1ColorLightHex: z.string(),
    tone1ColorLightName: z.string(),
    tone2ColorDarkHex: z.string(),
    tone2ColorDarkName: z.string(),
    tone2ColorLightHex: z.string(),
    tone2ColorLightName: z.string(),
    tone3ColorDarkHex: z.string(),
    tone3ColorDarkName: z.string(),
    tone3ColorLightHex: z.string(),
    tone3ColorLightName: z.string(),
    tone4ColorDarkHex: z.string(),
    tone4ColorDarkName: z.string(),
    tone4ColorLightHex: z.string(),
    tone4ColorLightName: z.string(),
    // Defaulted rather than required so a backup exported before the week-start
    // preference existed still imports: those files have no such key, and Monday
    // is the boundary their stats were computed with.
    weekStartsOn: z.enum(["sunday", "monday"]).default("monday"),
    // Settings → Daily New Cards; null is "No Limit". Defaulted so a backup exported before the
    // setting existed still imports. Without it every export, web backup and restore of a
    // database with this column failed on the unrecognized key.
    dailyNewGraphemeLimit: z.number().int().nonnegative().nullable().default(null),
  }),
);

export const actorDataExportSchema = z.strictObject({
  exportedAt: dateStringSchema,
  format: z.literal("han-data-export"),
  formatVersion: z.literal(1),
  tables: z.strictObject({
    collection: z.array(collectionSchema),
    collectionMorpheme: z.array(collectionMorphemeSchema),
    fsrsCard: z.array(fsrsCardSchema),
    fsrsCardReviewLog: z.array(fsrsCardReviewLogSchema),
    morphemeNote: z.array(morphemeNoteSchema),
    morphemeSearchVisit: z.array(morphemeSearchVisitSchema),
    studySession: z
      .array(
        z
          .strictObject({
            ...crudFields,
            endedAt: z.iso.datetime(),
            startedAt: z.iso.datetime(),
          })
          .refine((row) => row.endedAt >= row.startedAt, "Session end precedes start"),
      )
      .default([]),
    // Defaulted so exports written before rewards existed still import.
    rewardClaim: z
      .array(
        z.strictObject({
          ...crudFields,
          claimedAt: dateStringSchema,
          claimedOn: z.iso.date(),
          dragonAmount: z.int(),
          hanAmount: z.int(),
          // Defaulted so exports written before HanMiles and the review bonus still import.
          hanMilesAmount: z.int().default(0),
          kind: z.string().min(1),
          reviewBonusHan: z.int().default(0),
        }),
      )
      .default([]),
    // Defaulted so exports written before quests existed still import.
    userQuest: z
      .array(
        z.strictObject({
          ...crudFields,
          adcode: z.int(),
          completedAt: z.nullable(dateStringSchema),
          costHan: z.int().nonnegative(),
          questId: z.string().min(1),
          unlockedAt: dateStringSchema,
          xpEarned: z.int().nonnegative(),
        }),
      )
      .default([]),
    // Defaulted so exports written before travel existed still import.
    travelTrip: z
      .array(
        z
          .strictObject({
            ...crudFields,
            arrivesAt: dateStringSchema,
            cancelledAt: z.nullable(dateStringSchema),
            costHan: z.int().nonnegative(),
            departedAt: dateStringSchema,
            destinationAdcode: z.int(),
            distanceKm: z.int().nonnegative(),
            fareFen: z.int().nonnegative(),
            // Defaulted for exports written before HanMiles existed.
            hanMilesRedeemed: z.int().nonnegative().default(0),
            mode: z.string().min(1),
            originAdcode: z.int(),
            seatClass: z.nullable(z.string()),
          })
          .refine((row) => row.arrivesAt >= row.departedAt, "Trip arrives before it departs"),
      )
      .default([]),
    user: z.array(userSchema).min(1),
    userCard: z.array(userCardSchema),
    userCardReview: z.array(userCardReviewSchema),
    userGraphemeMeaningCard: z.array(userGraphemeMeaningCardSchema),
    userGraphemePronunciationCard: z.array(userGraphemePronunciationCardSchema),
    // Defaulted so exports written before quiz cards existed still import.
    userQuizCard: z.array(userQuizCardSchema).default([]),
    userSentence: z.array(userSentenceSchema),
    userSlide: z.array(userSlideSchema),
    userWord: z
      .array(z.strictObject({ ...crudFields, locked: z.boolean(), word: z.string() }))
      .default([]),
    userWordSentenceCard: z.array(userWordSentenceCardSchema),
  }),
});

export type ActorDataExport = z.infer<typeof actorDataExportSchema>;
